import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import Starfield from "../components/Starfield";
import { DealCard, ResultsCounter, useResults } from "../components/ResultsWall";
import { ArrowRight } from "lucide-react";

const FILTERS = [["all", "All"], ["tiktok", "TikTok"], ["instagram", "Instagram"], ["youtube", "YouTube"]];

export default function Results() {
  const data = useResults(400);
  const [filter, setFilter] = useState("all");
  const deals = useMemo(() => (data?.deals || []).filter((d) => filter === "all" || d.platform === filter), [data, filter]);

  return (
    <div className="min-h-screen grain relative">
      <Starfield />
      <SEO title="Results · Completed creator deals on Nexus" description="Real brand × creator deals funded through escrow, delivered, approved and paid out on Nexus." path="/results" />
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-12 space-y-10 relative" data-testid="results-page">
        <header className="max-w-2xl">
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">Results</div>
          <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tighter">Deals that got <span className="nexus-text-gradient">funded, delivered & paid</span>.</h1>
          <p className="text-zinc-400 mt-4 text-base">Every card below went through the full Nexus flow: brand verified, escrow funded via Stripe, video reviewed in-app, approved, creator paid. No agency fees, no chasing invoices.</p>
        </header>
        {data && <ResultsCounter totals={data.totals} />}
        <div className="flex items-center gap-2 flex-wrap" data-testid="results-filters">
          {FILTERS.map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)} className={`px-4 h-9 rounded-full text-xs uppercase tracking-widest border transition-colors ${filter === k ? "nexus-gradient text-white border-transparent" : "border-white/10 text-zinc-400 hover:text-white"}`} data-testid={`results-filter-${k}`}>{l}</button>
          ))}
          <span className="ml-auto text-xs text-zinc-500" data-testid="results-count">{deals.length} deals</span>
        </div>
        {!data ? (
          <div className="text-zinc-500 py-20 text-center">Loading…</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="results-grid">
            {deals.map((d) => <DealCard key={d.id} d={d} />)}
          </div>
        )}
        <section className="rounded-3xl nexus-gradient p-[1px]">
          <div className="rounded-3xl bg-[#0A0A0F] p-8 md:p-12 flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <h2 className="font-display font-bold text-2xl md:text-3xl tracking-tight">Your deal could be next on this wall.</h2>
              <p className="text-zinc-400 mt-2 text-sm">Brands post a brief in 5 minutes. Creators apply in one tap. Escrow keeps everyone honest.</p>
            </div>
            <div className="flex gap-3 flex-wrap">
              <Link to="/auth?mode=signup&role=brand" className="inline-flex items-center gap-2 h-11 px-5 rounded-full nexus-gradient text-white font-semibold text-sm" data-testid="results-cta-brand">Post a brief <ArrowRight className="h-4 w-4" /></Link>
              <Link to="/auth?mode=signup&role=creator" className="inline-flex items-center gap-2 h-11 px-5 rounded-full border border-white/15 text-white font-semibold text-sm hover:bg-white/5" data-testid="results-cta-creator">Join as a creator</Link>
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </div>
  );
}
